import {
  Target, Eye, Award, ShieldCheck, Clock, Heart, Sparkles, MapPin,
} from "lucide-react";
import { useScrollReveal } from "../lib/hooks";
import { PageHero, SectionBadge, CTABanner } from "../components/Shared";
import { HERO_IMG, FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const VALUES = [
  {
    Icon: Award,
    title: "Excellence",
    text: "We hold ourselves to five-star hospitality standards. 'Good enough' is never good enough — every space we touch must be spotless.",
  },
  {
    Icon: ShieldCheck,
    title: "Integrity & Trust",
    text: "We enter your home and your business. That trust is sacred. Our team is licensed, insured, and background-checked.",
  },
  {
    Icon: Clock,
    title: "Reliability",
    text: "We show up on time, every time, exactly as promised. Your schedule is our commitment — no excuses, no surprises.",
  },
  {
    Icon: Sparkles,
    title: "Attention to Detail",
    text: "From ceiling corners to baseboards, the details are where excellence lives. It's what separates a clean space from a Cindyrella clean.",
  },
  {
    Icon: Heart,
    title: "Care for Our Community",
    text: "Naples is our home. We treat every client like a neighbor — because they are. Local, family-driven, and proud of it.",
  },
];

const StorySection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="about-story-section" className="py-16 md:py-24 section-hidden" style={{ background: "#FFFFFF" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <SectionBadge>Our Story</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
              A Naples Company Built on Trust
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
              Cindyrella Cleaning LLC was born in Naples, Florida with a simple
              conviction: cleaning is not just a task — it's an act of care.
              What started with a few local homes has grown into a trusted team
              serving luxury residences, Airbnb properties, restaurants,
              and offices across Naples.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
              Our growth has come the old-fashioned way — one spotless space,
              one happy client, one referral at a time. Today, families and
              businesses across Collier County rely on us because we treat
              their spaces the way we'd treat our own.
            </p>
            <div className="flex items-center gap-2">
              <MapPin size={16} style={{ color: GOLD }} />
              <span className="text-sm font-semibold" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>
                Proudly based in Naples, FL — Licensed &amp; Insured
              </span>
            </div>
          </div>
          <div>
            <div className="overflow-hidden" style={{ boxShadow: `14px 14px 0px ${GOLD}, 0 10px 45px rgba(27,58,107,0.16)` }}>
              <img src={HERO_IMG} alt="Pristine Naples FL home cleaned by Cindyrella Cleaning LLC — licensed and insured cleaning team" loading="lazy" className="w-full h-72 md:h-96 object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MissionVisionSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="about-mission-section" className="py-16 md:py-24 section-hidden" style={{ background: NAVY }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-8 md:p-10" data-testid="mission-card" style={{ background: "rgba(255,255,255,0.06)", borderTop: `4px solid ${GOLD}`, backdropFilter: "blur(10px)" }}>
            <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ background: "rgba(212,168,67,0.18)", borderRadius: "2px" }}>
              <Target size={26} style={{ color: GOLD }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>
              Our Mission
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}>
              To deliver five-star cleaning experiences that give Naples
              families and businesses back their most valuable asset — time —
              while providing spaces so impeccably clean they inspire pride,
              comfort, and peace of mind.
            </p>
          </div>

          <div className="p-8 md:p-10" data-testid="vision-card" style={{ background: "rgba(255,255,255,0.06)", borderTop: `4px solid ${GOLD}`, backdropFilter: "blur(10px)" }}>
            <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ background: "rgba(212,168,67,0.18)", borderRadius: "2px" }}>
              <Eye size={26} style={{ color: GOLD }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>
              Our Vision
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}>
              To be Southwest Florida's most trusted name in professional
              cleaning — the first call for every luxury home, vacation rental,
              restaurant, and office that refuses to settle for
              anything less than excellence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const ValueCard = ({ Icon, title, text, idx }) => (
  <div
    data-testid={`value-card-${idx}`}
    className="bg-white p-8 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl"
    style={{ border: "1px solid rgba(27,58,107,0.08)", borderLeft: `4px solid ${GOLD}` }}
  >
    <div className="w-12 h-12 flex items-center justify-center mb-5" style={{ background: "rgba(27,58,107,0.08)", borderRadius: "2px" }}>
      <Icon size={22} style={{ color: NAVY }} />
    </div>
    <h3 className="text-lg font-bold mb-3" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      {title}
    </h3>
    <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      {text}
    </p>
  </div>
);

const ValuesQuoteCard = () => (
  <div
    data-testid="values-quote-card"
    className="p-8 flex flex-col justify-center"
    style={{ background: NAVY, borderLeft: `4px solid ${GOLD}` }}
  >
    <p className="text-base italic leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.9)", fontFamily: FONT_BODY }}>
      "Our standard is simple: leave every space better than we found
      it, every single time."
    </p>
    <p className="text-xs font-bold tracking-wider" style={{ color: GOLD, fontFamily: FONT_HEAD }}>
      — Cindyrella Cleaning LLC
    </p>
  </div>
);

const ValuesSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="about-values-section" className="py-16 md:py-24 section-hidden" style={{ background: "#F9FAFB" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>What Guides Us</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Our Core Values
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Five principles behind every clean — and every client relationship
            we build in Naples.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUES.map((value, idx) => (
            <ValueCard key={value.title} {...value} idx={idx} />
          ))}
          <ValuesQuoteCard />
        </div>
      </div>
    </section>
  );
};

const StatsSection = () => {
  const ref = useScrollReveal();
  const STATS = [
    { value: "5.0", label: "Average Client Rating" },
    { value: "60s", label: "Free Online Estimate" },
    { value: "100%", label: "Licensed & Insured" },
    { value: "24h", label: "Quote Response Time" },
  ];
  return (
    <section ref={ref} data-testid="about-stats-section" className="py-14 section-hidden" style={{ background: "#FFFFFF" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map(({ value, label }) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
                {value}
              </div>
              <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: GOLD, fontFamily: FONT_BODY }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function AboutPage() {
  return (
    <>
      <PageHero
        testid="about-page-hero"
        badge="About Us"
        title={<>The Team Behind the <span style={{ color: GOLD }}>Shine</span></>}
        subtitle="Cindyrella Cleaning LLC — a Naples, FL company built on trust, detail, and a genuine love for what we do."
      />
      <StorySection />
      <MissionVisionSection />
      <ValuesSection />
      <StatsSection />
      <CTABanner testid="about-cta-banner" />
    </>
  );
}
